import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, Eye, RefreshCw, Sparkles, Timer, Truck, Wallet, Flame, MessageCircle } from 'lucide-react';
import { cn, formatPrice } from '@/lib/utils';
import ProductImage from '@/components/ProductImage';

/* ------------------------------------------------------------------ */
/* شريط الثقة                                                          */
/* ------------------------------------------------------------------ */
export const TrustBar = ({ deliveryFee }) => {
  const items = [
    { icon: Truck, title: 'توصيل لجميع المناطق', text: `رسوم ثابتة ${formatPrice(deliveryFee)}` },
    { icon: Wallet, title: 'الدفع عند الاستلام', text: 'ادفعي لما يوصلك طلبك' },
    { icon: Eye, title: 'معاينة قبل الدفع', text: 'شوفي المنتج بإيدك أولاً' },
    { icon: RefreshCw, title: 'استبدال واسترجاع', text: 'حسب سياسة الاسترجاع' },
  ];
  return (
    <section className="relative z-10 -mt-8 md:-mt-10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
        <div className="grid grid-cols-2 lg:grid-cols-4 rounded-3xl bg-white shadow-xl shadow-burgundy-500/[0.07] ring-1 ring-gray-100 overflow-hidden">
          {items.map(({ icon: Icon, title, text }, i) => (
            <div
              key={title}
              className={cn(
                'flex flex-col sm:flex-row items-center gap-2 sm:gap-3 md:gap-4 p-4 md:p-6 text-center sm:text-right',
                i % 2 === 0 && 'border-l border-gray-100',
                i < 2 && 'border-b lg:border-b-0 border-gray-100',
                i === 1 && 'lg:border-l'
              )}
            >
              <span className="shrink-0 w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-burgundy-500 to-burgundy-600 text-brand-gold flex items-center justify-center shadow-md shadow-burgundy-500/20">
                <Icon className="w-5 h-5 md:w-7 md:h-7" />
              </span>
              <div className="min-w-0">
                <p className="font-tajawal font-bold text-[13px] sm:text-sm md:text-base text-brand-black leading-snug">{title}</p>
                <p className="font-cairo text-[11px] md:text-sm text-gray-500 mt-0.5">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* عنوان قسم موحّد                                                      */
/* ------------------------------------------------------------------ */
export const SectionHeading = ({ eyebrow, title, action }) => (
  <div className="flex items-end justify-between gap-4 mb-6 md:mb-10">
    <div>
      {eyebrow && (
        <p className="inline-flex items-center gap-2 text-xs md:text-sm font-cairo font-bold text-brand-gold mb-2 tracking-wide">
          <span className="w-6 h-px bg-brand-gold" />
          {eyebrow}
        </p>
      )}
      <h2 className="text-2xl md:text-4xl font-tajawal font-extrabold text-brand-black">{title}</h2>
    </div>
    {action}
  </div>
);

/* ------------------------------------------------------------------ */
/* تسوّقي حسب القسم                                                    */
/* ------------------------------------------------------------------ */
// درجات متناسقة من هوية الماركة لكروت الأقسام
const TILE_THEMES = [
  'from-burgundy-500 to-burgundy-600 text-white',
  'from-[#f3e6dc] to-[#ead6c6] text-brand-black',
  'from-[#2a1416] to-[#4a1d22] text-white',
  'from-brand-gold to-[#b8912a] text-brand-black',
  'from-[#8d4a52] to-burgundy-500 text-white',
  'from-[#efe7e1] to-[#e2d3c8] text-brand-black',
];

export const CategoryTiles = ({ categories, images = {} }) => {
  if (!categories?.length) return null;
  return (
    <section className="py-12 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
        <SectionHeading eyebrow="اختاري ستايلك" title="تسوّقي حسب القسم" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {categories.map((category, i) => {
            const theme = TILE_THEMES[i % TILE_THEMES.length];
            const image = images[category.id];
            return (
              <Link
                key={category.id}
                to={`/products?categories=${category.id}`}
                className={cn(
                  'group relative aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1',
                  theme
                )}
              >
                {image ? (
                  <>
                    <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                  </>
                ) : (
                  <>
                    <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(currentColor 1px, transparent 1.2px)', backgroundSize: '18px 18px', color: 'rgba(255,255,255,0.18)' }} />
                    {/* حرف القسم كعنصر زخرفي كبير */}
                    <span className="absolute -left-3 -bottom-10 font-tajawal font-extrabold text-[9rem] leading-none opacity-[0.12] select-none transition-transform duration-700 group-hover:scale-110">
                      {category.name.trim().charAt(0)}
                    </span>
                  </>
                )}
                <div className={cn('absolute inset-x-0 bottom-0 p-4 md:p-5', image && 'text-white')}>
                  <p className="font-tajawal font-extrabold text-lg md:text-2xl leading-tight">{category.name}</p>
                  <p className="font-cairo text-xs md:text-sm opacity-80 mt-1">{category.count} منتج</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs md:text-sm font-cairo font-bold opacity-90 group-hover:gap-2 transition-all">
                    تسوّقي الآن <ArrowLeft className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* عرض اليوم مع عدّاد تنازلي لنهاية اليوم                               */
/* ------------------------------------------------------------------ */
// عدّاد تنازلي لموعد انتهاء الخصم اللي الأدمن حدده
const useCountdown = (endsAt, onExpire) => {
  const calc = () => {
    const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
    return {
      done: diff === 0,
      d: Math.floor(diff / 8.64e7),
      h: Math.floor((diff % 8.64e7) / 3.6e6),
      m: Math.floor((diff % 3.6e6) / 6e4),
      s: Math.floor((diff % 6e4) / 1000),
    };
  };
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    setLeft(calc());
    const t = setInterval(() => {
      const next = calc();
      setLeft(next);
      if (next.done) {
        clearInterval(t);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return left;
};

const Countdown = ({ endsAt, onExpire }) => {
  const { d, h, m, s } = useCountdown(endsAt, onExpire);
  const parts = [
    ...(d > 0 ? [[d, 'يوم']] : []),
    [h, 'ساعة'],
    [m, 'دقيقة'],
    [s, 'ثانية'],
  ];
  return (
    <div className="flex items-center justify-center md:justify-start gap-2 md:gap-3 mb-8" aria-label="الوقت المتبقي على انتهاء العرض">
      <Timer className="w-5 h-5 text-brand-gold ml-1 hidden sm:block" />
      {parts.map(([value, label], i) => (
        <span key={label} className="flex items-center gap-2 md:gap-3">
          {i > 0 && <span className="text-2xl text-white/40 -mt-5">:</span>}
          <TimeBox value={value} label={label} />
        </span>
      ))}
    </div>
  );
};

const TimeBox = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/10 ring-1 ring-white/15 backdrop-blur flex items-center justify-center font-tajawal font-extrabold text-2xl md:text-3xl tabular-nums text-white">
      {String(value).padStart(2, '0')}
    </span>
    <span className="mt-1.5 text-[11px] font-cairo text-white/70">{label}</span>
  </div>
);

export const DealOfTheDay = ({ product, onExpire }) => {
  if (!product) return null;
  const endsAt = product.sale_ends_at;

  const saving = product.original_price - product.price;

  return (
    <section className="py-6 md:py-10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-l from-burgundy-600 via-burgundy-500 to-[#3d0a0f] shadow-2xl shadow-burgundy-500/20">
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(212,175,55,0.2) 1px, transparent 1.4px)', backgroundSize: '22px 22px' }} />
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-brand-gold/10 blur-2xl" />

          <div className="relative grid md:grid-cols-5 items-center gap-6 md:gap-10 p-6 md:p-12">
            {/* الصورة */}
            <Link to={`/products/${product.id}`} className="md:col-span-2 group relative block">
              <div className="relative aspect-[4/5] max-w-xs mx-auto rounded-3xl overflow-hidden ring-4 ring-white/10 shadow-2xl rotate-[-2deg] group-hover:rotate-0 transition-transform duration-500">
                <ProductImage
                  src={product.images?.[0]?.url}
                  name={product.name_ar}
                  className="w-full h-full object-cover"
                  textClassName="text-3xl"
                />
              </div>
              <span className="absolute -top-3 right-6 md:right-10 w-20 h-20 md:w-24 md:h-24 rounded-full bg-brand-gold text-brand-black flex flex-col items-center justify-center shadow-xl rotate-12 hero-float">
                <span className="font-tajawal font-extrabold text-2xl md:text-3xl leading-none">{product.discount_percentage}%</span>
                <span className="font-cairo text-[11px] font-bold">خصم</span>
              </span>
            </Link>

            {/* التفاصيل */}
            <div className="md:col-span-3 text-white text-center md:text-right">
              <p className="inline-flex items-center gap-2 bg-white/10 ring-1 ring-white/15 rounded-full px-4 py-1.5 text-sm font-cairo font-bold text-brand-gold mb-4">
                <Flame className="w-4 h-4" /> {endsAt ? 'عرض لفترة محدودة' : 'عرض مميز'}
              </p>
              <h2 className="font-tajawal font-extrabold text-3xl md:text-5xl mb-3">{product.name_ar}</h2>
              <div className="flex items-baseline justify-center md:justify-start gap-3 mb-2">
                <span className="font-tajawal font-extrabold text-4xl md:text-5xl text-brand-gold">{formatPrice(product.price)}</span>
                <span className="font-cairo text-lg text-white/50 line-through">{formatPrice(product.original_price)}</span>
              </div>
              <p className="font-cairo text-white/80 mb-6">
                وفّري <span className="font-bold text-white">{formatPrice(saving)}</span> على هذه القطعة
              </p>

              {endsAt ? (
                <Countdown endsAt={endsAt} onExpire={onExpire} />
              ) : (
                <div className="mb-8" />
              )}

              <Link
                to={`/products/${product.id}`}
                className="group inline-flex items-center gap-2 h-14 px-9 rounded-full bg-brand-gold text-brand-black font-cairo font-bold text-lg shadow-xl hover:bg-white transition-all hover:-translate-y-0.5"
              >
                اطلبيها الآن
                <ChevronLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* بانرين جنب بعض                                                       */
/* ------------------------------------------------------------------ */
export const PromoBanners = ({ newCount, newDays, saleCount }) => {
  // كل بانر بيظهر بس لو وراه بيانات حقيقية
  if (!newCount && !saleCount) return null;
  const both = newCount > 0 && saleCount > 0;
  return (
  <section className="py-6 md:py-10">
    <div className={cn('container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px] grid gap-4 md:gap-6', both && 'md:grid-cols-2')}>
      {newCount > 0 && (
      <Link
        to="/products?sort=newest"
        className="group relative overflow-hidden rounded-[2rem] bg-[#f3e6dc] min-h-[220px] md:min-h-[280px] p-7 md:p-10 flex flex-col justify-center"
      >
        <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-burgundy-500/10 transition-transform duration-700 group-hover:scale-125" />
        <div className="absolute left-8 top-8 w-20 h-20 rounded-full border-[10px] border-brand-gold/30" />
        <Sparkles className="absolute left-10 bottom-10 w-16 h-16 md:w-24 md:h-24 text-burgundy-500/15 transition-transform duration-700 group-hover:rotate-12" />
        <div className="relative max-w-xs">
          <p className="text-sm font-cairo font-bold text-burgundy-500 mb-2">وصل حديثاً</p>
          <h3 className="font-tajawal font-extrabold text-3xl md:text-4xl text-brand-black leading-[1.35] mb-3">
            موديلات جديدة
            <br />
            وصلت للتو
          </h3>
          <p className="font-cairo text-gray-600 mb-5">{newCount} قطعة جديدة خلال آخر {newDays} يوم</p>
          <span className="inline-flex items-center gap-2 font-cairo font-bold text-burgundy-600 group-hover:gap-3 transition-all">
            اكتشفي الجديد <ArrowLeft className="w-5 h-5" />
          </span>
        </div>
      </Link>
      )}

      {saleCount > 0 && (
      <Link
        to="/products?on_sale=1&sort=discount"
        className="group relative overflow-hidden rounded-[2rem] bg-brand-black min-h-[220px] md:min-h-[280px] p-7 md:p-10 flex flex-col justify-center"
      >
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: 'radial-gradient(rgba(212,175,55,0.25) 1px, transparent 1.4px)', backgroundSize: '20px 20px' }} />
        <div className="absolute -left-10 top-1/2 -translate-y-1/2 font-tajawal font-extrabold text-[10rem] md:text-[13rem] leading-none text-brand-gold/15 select-none transition-transform duration-700 group-hover:scale-110">
          %
        </div>
        <div className="relative max-w-xs">
          <p className="text-sm font-cairo font-bold text-brand-gold mb-2">عروض مميزة</p>
          <h3 className="font-tajawal font-extrabold text-3xl md:text-4xl text-white leading-[1.35] mb-3">
            خصومات على
            <br />
            تشكيلة مختارة
          </h3>
          <p className="font-cairo text-white/70 mb-5">{saleCount} قطعة عليها خصم الآن</p>
          <span className="inline-flex items-center gap-2 font-cairo font-bold text-brand-gold group-hover:gap-3 transition-all">
            تسوّقي العروض <ArrowLeft className="w-5 h-5" />
          </span>
        </div>
      </Link>
      )}
    </div>
  </section>
  );
};

/* ------------------------------------------------------------------ */
/* بانر استشارة واتساب                                                  */
/* ------------------------------------------------------------------ */
export const WhatsAppCta = ({ whatsappUrl }) => {
  if (!whatsappUrl) return null;
  return (
    <section className="py-12 md:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-l from-[#f7efe9] to-[#efe2d8] p-8 md:p-12 flex flex-col md:flex-row items-center gap-6 md:gap-10 text-center md:text-right">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-emerald-500/10" />
          <span className="relative shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-emerald-500 text-white flex items-center justify-center shadow-xl shadow-emerald-500/30 rotate-[-6deg]">
            <MessageCircle className="w-10 h-10 md:w-12 md:h-12" />
          </span>
          <div className="relative flex-1">
            <h3 className="font-tajawal font-extrabold text-2xl md:text-4xl text-brand-black mb-2">محتارة في المقاس أو الموديل؟</h3>
            <p className="font-cairo text-gray-600 md:text-lg">راسلينا على واتساب وفريقنا بيساعدك تختاري القطعة المناسبة لكِ.</p>
          </div>
          <a
            href={`${whatsappUrl}?text=${encodeURIComponent('مرحباً، أحتاج مساعدة في اختيار قطعة')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="relative shrink-0 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-cairo font-bold text-lg shadow-lg shadow-emerald-500/25 transition hover:-translate-y-0.5"
          >
            <MessageCircle className="w-5 h-5" />
            تواصلي معنا
          </a>
        </div>
      </div>
    </section>
  );
};
